import { useState } from 'react'
import isEqual from 'lodash/isEqual'

import { Button } from '@wartek-id/button-v1'
import type {
  CustomListProps as WartekCustomListProps,
  OptionItem,
} from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'

import { translateTargetWaktu } from 'app/KinerjaPage/utils/kinerja'
import AdjustmentInfo from '../AdjustmentInfo'

import type { PropsWithChildren } from 'react'
import type {
  KinerjaPraktikPembelajaranContentIndikator,
  KinerjaSelection,
  KinerjaUtamaDetail,
} from 'types/kinerja'

interface KinerjaDropdownProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: KinerjaUtamaDetail[]
  showDeleteConfirmation?: boolean
  disableDeleteButton?: boolean
  onChange?: (selectedOption: KinerjaSelection) => void
  onDelete?: () => void
  value?: KinerjaSelection
  originalKinerjaNumber?: number
  originalKinerja?:
    | KinerjaUtamaDetail
    | KinerjaPraktikPembelajaranContentIndikator
  kinerjaNumber?: number
  kinerja?: KinerjaUtamaDetail | KinerjaPraktikPembelajaranContentIndikator
  showChanges?: boolean
  showPoint?: boolean
  defaultUnselectedTargetQty?: string
  adjustmentInfoText?: string
  showCheckButton?: boolean
}

const createOptionTargetKuantitas = (number: number) => ({
  name: `${number} kegiatan`,
  option: number,
})

interface CustomOptionItem extends OptionItem {
  option: KinerjaUtamaDetail
}

interface CustomListProps extends WartekCustomListProps {
  item: OptionItem
  showPoint?: boolean
}

const CustomList = ({ item, active, selected, showPoint }: CustomListProps) => {
  const { name, option } = item
  const getColor = () => {
    if (active) return 'inverse'

    return 'default'
  }

  const getIconColor = () => {
    if (active) return 'inverse'

    return 'success'
  }

  const getVariant = () => {
    if (selected) return 'body-bold'

    return 'body'
  }

  return (
    <div className="flex justify-between -mr-4">
      <Text color={getColor()} variant={getVariant()}>
        {name} {showPoint && <strong>({option?.bobot} poin)</strong>}
      </Text>
      {selected && (
        <Icon fontSize="sm" color={getIconColor()}>
          check
        </Icon>
      )}
    </div>
  )
}

const CustomDisplayValue = ({ item, showPoint }: CustomListProps) => {
  const { name, option } = item

  return (
    <Text as="span">
      {name} {showPoint && `(${option?.bobot} poin)`}
    </Text>
  )
}

export default function KinerjaDropdown({
  options,
  showDeleteConfirmation = false,
  disableDeleteButton = false,
  onDelete = () => {},
  onChange = () => {},
  value,
  originalKinerjaNumber,
  originalKinerja,
  kinerjaNumber = 1,
  kinerja,
  showChanges = false,
  showPoint = false,
  defaultUnselectedTargetQty,
  adjustmentInfoText,
  showCheckButton,
  ...props
}: KinerjaDropdownProps) {
  const formattedOptions = options.map((option) => ({
    name: option.rencanaHasilKerja,
    option,
  }))

  const selectedOption = options.find((option) => option.id === value?.id)

  const optionsTargetKuantitas = Array.from(
    {
      length: selectedOption?.targetKuantitasMax,
    },
    (_, i) => createOptionTargetKuantitas(i + 1)
  )

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)

  const openDeleteConfirmation = () => {
    if (showDeleteConfirmation) setIsDeleteConfirmationOpen(true)
    else {
      onDelete()
    }
  }

  const hasChanges = !isEqual(originalKinerja, kinerja)

  return (
    <div
      className="bg-blue-5 border border-gray-30 rounded p-6 flex flex-col gap-6"
      {...props}
    >
      {showChanges && hasChanges && (
        <AdjustmentInfo
          data-testid="adjustment-info"
          text={adjustmentInfoText}
          showCheckButton={showCheckButton}
          originalKinerja={originalKinerja}
          kinerja={kinerja}
          kinerjaNumber={kinerjaNumber}
          originalKinerjaNumber={originalKinerjaNumber}
        />
      )}
      <div className="flex gap-2.5">
        <Text variant="heading-sm" color="primary">
          {kinerjaNumber}.
        </Text>
        <div className="w-full flex-1">
          <Text className="mb-1">Rencana Hasil Kerja</Text>
          <div className="flex gap-3 w-full">
            <div className="flex-1">
              <CustomSelect
                placeholder="Pilih"
                data-testid="select-kinerja"
                customList={(p: PropsWithChildren<CustomListProps>) =>
                  CustomList({ ...p, showPoint })
                }
                customDisplayValue={(p: PropsWithChildren<CustomListProps>) =>
                  CustomDisplayValue({ ...p, showPoint })
                }
                value={
                  selectedOption
                    ? {
                        name: selectedOption.rencanaHasilKerja,
                        option: selectedOption,
                      }
                    : undefined
                }
                options={formattedOptions}
                onChange={(option) => {
                  onChange({
                    id: (option as CustomOptionItem).option.id,
                    kuantitas: (option as CustomOptionItem).option
                      .targetKuantitasMin,
                  })
                }}
              />
              {!!selectedOption?.catatan && (
                <Text variant="helper" color="subdued" className="mt-1">
                  Catatan: {selectedOption.catatan}
                </Text>
              )}
            </div>

            <Button
              color="white"
              size="lg"
              className="!bg-blue-5 self-start"
              disabled={disableDeleteButton}
              onClick={() => openDeleteConfirmation()}
            >
              Hapus
            </Button>
          </div>
        </div>
      </div>
      <div className="px-9 flex gap-6">
        <div className="w-full">
          <Text color="subdued">Bukti Dukung</Text>
          {selectedOption?.buktiDukung.map((buktiDukung, i, allBuktiDukung) => (
            <Text key={`{fieldName}.buktiDukung.${i}`} variant="action">
              {allBuktiDukung.length > 1 && `${i + 1}. `}
              {buktiDukung.deskripsi}
            </Text>
          )) ?? '-'}
        </div>

        <div className="w-full">
          <Text color="subdued">Target Kuantitas</Text>
          {!selectedOption && !!defaultUnselectedTargetQty ? (
            <Text variant="action">{defaultUnselectedTargetQty}</Text>
          ) : !selectedOption ? (
            <CustomSelect
              className="mt-1"
              placeholder="Pilih"
              isDisabled
              options={[]}
            />
          ) : selectedOption.targetKuantitasMin ===
            selectedOption.targetKuantitasMax ? (
            <Text variant="action">{selectedOption.targetKuantitas}</Text>
          ) : (
            <CustomSelect
              className="mt-1"
              placeholder="Pilih"
              data-testid="select-kinerja-kuantitas"
              isDisabled={!selectedOption}
              value={
                selectedOption
                  ? createOptionTargetKuantitas(value.kuantitas)
                  : undefined
              }
              options={optionsTargetKuantitas}
              onChange={(option) => {
                onChange({
                  id: selectedOption.id,
                  kuantitas: (option as OptionItem).option,
                })
              }}
            />
          )}
        </div>

        <div className="w-full">
          <Text color="subdued">Target Waktu</Text>
          <Text variant="action">
            {translateTargetWaktu(selectedOption?.targetWaktu ?? 6)}
          </Text>
        </div>

        {showPoint && (
          <div className="w-full">
            <Text color="subdued">Poin</Text>
            <Text variant="action">
              {!!selectedOption && value?.kuantitas
                ? selectedOption?.bobot * value.kuantitas
                : '-'}
            </Text>
          </div>
        )}
      </div>

      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={setIsDeleteConfirmationOpen}
        size="sm"
      >
        <DialogOverlay />
        <DialogContent className="px-6 pt-8 pb-6 !rounded-lg">
          <div className="mb-7">
            <Text className="mb-4" variant="display-sm">
              Hapus Rencana Hasil Kerja ini?
            </Text>

            <Text variant="body-lg" color="subdued">
              Jika dihapus, Anda harus menambah ulang Rencana Hasil Kerja jika
              diperlukan. Tetap hapus?
            </Text>
          </div>
          <div className="flex gap-3">
            <Button
              size="lg"
              color="red"
              onClick={() => {
                onDelete()
                setIsDeleteConfirmationOpen(false)
              }}
            >
              Ya, hapus
            </Button>
            <DialogCancel
              as={Button}
              size="lg"
              color="white"
              onClick={() => setIsDeleteConfirmationOpen(false)}
            >
              Batal
            </DialogCancel>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
